import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router'

/**
 * Middleware function type
 * Returns true to continue, false to cancel, or a route location to redirect
 */
export type MiddlewareFn = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
) => true | false | RouteLocationRaw | Promise<true | false | RouteLocationRaw>

/**
 * Middleware registry - maps names to functions
 */
const middlewareRegistry = new Map<string, MiddlewareFn>()

/**
 * Register a middleware by name
 */
export function defineMiddleware(name: string, fn: MiddlewareFn): void {
  middlewareRegistry.set(name, fn)
}

/**
 * Get middleware by name
 */
export function getMiddleware(name: string): MiddlewareFn | undefined {
  return middlewareRegistry.get(name)
}

/**
 * Executes middleware pipeline based on route meta
 * Only runs middleware that the route explicitly requests
 */
export async function runMiddlewarePipeline(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
): Promise<true | false | RouteLocationRaw> {
  // Collect middleware from all matched route records
  const middlewareNames = to.matched.flatMap((record) => record.meta.middleware ?? [])

  // Deduplicate while preserving order
  const uniqueNames = [...new Set(middlewareNames)]

  for (const name of uniqueNames) {
    const middleware = getMiddleware(name)

    if (!middleware) {
      console.warn(`[Router] Middleware "${name}" not found in registry`)
      continue
    }

    const result = await middleware(to, from)

    if (result !== true) {
      return result
    }
  }

  return true
}
